import ModalContainer from 'containers/ProductRegist/components/common/ModalContainer';
import { ICON_CLOSE } from 'assets/icons';
import Text from 'components/common/Text';
import CommonImage from 'components/common/CommonImage';
import styled from 'styled-components';
import AuthButton from 'components/Button/AuthButton';
import { Option } from '../../types/common.types';
import dayjs, { Dayjs } from 'dayjs';

import { DesktopDatePicker, DatePicker } from '@mui/x-date-pickers';
import { OrderFilterInput } from './graphQL/types';
import { useRef } from 'react';

const ModalHeader = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;

  height: 76px;
  padding: 24px 20px;
`;

const ModalBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;

  padding: 0px 20px 48px;
`;

const ModalFooter = styled.footer`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;

  position: sticky;
  bottom: 0;
  padding: 16px 20px;
  margin-top: auto;

  width: 100%;
  background-color: inherit;
  border-radius: 0 0 16px 16px;
  border-top: 1px solid #dbdcdf;
`;

const InitButton = styled.button<{ $isAbled: boolean }>`
  padding: 12px;
  width: auto;
  min-width: 76px;

  border-radius: 12px;
  border: none;
  background: #f4f4f5;
  color: ${props => (props.$isAbled ? 'rgba(70, 71, 76, 1)' : '#dbdcdf')};

  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
  letter-spacing: -0.6px;

  cursor: pointer;
`;

const PeriodButtonBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 13px;

  height: 44px;
  padding: 6px;
  border-radius: 12px;

  background-color: rgba(244, 244, 245, 1);
`;

const PeriodButton = styled.button<{ $isSelected: boolean }>`
  width: 100%;
  height: 100%;

  border-radius: 8px;
  border: none;

  font-size: 14px;
  font-weight: 600;
  line-height: 32px;
  letter-spacing: -0.6px;

  ${props => `
    background: ${props.$isSelected ? 'rgba(255, 255, 255, 1)' : 'inherit'};
    color: ${
      props.$isSelected ? 'rgba(27, 28, 30, 1)' : 'rgba(152, 155, 162, 1)'
    };
    box-shadow: ${
      props.$isSelected
        ? 'box-shadow: 0px 0px 5px 0px rgba(0, 0, 0, 0.05)'
        : 'none'
    };

    
  `};

  cursor: pointer;
`;

const DatePickerBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;

  height: 44px;

  border-radius: 8px;
  border: 1px solid;
  border-color: rgba(234, 235, 236, 1);

  background-color: white;
`;

const DatePickerWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  position: relative;
  z-index: 100;

  width: 100%;
  border: 0px;

  > .MuiTextField-root {
    height: 100%;
    .MuiOutlinedInput-root {
      height: 100%;
    }

    fieldset {
      border: none;
    }
  }

  .MuiButtonBase-root {
    position: absolute;
    top: 0;
    left: 0;

    border-radius: 0px;

    width: 100%;
    height: 100%;

    > svg {
      position: absolute;
      right: 20px;
    }
  }
`;

const PERIOD_BUTTON_LIST = [
  {
    label: '직접설정',
    value: 'custom',
  },
  {
    label: '오늘',
    value: 'today',
  },
  {
    label: '1개월',
    value: '1month',
  },
  {
    label: '1년',
    value: '1year',
  },
];

interface Props {
  isMobile: boolean;
  handleDetailModal: () => void;
  handlePeriodButtonClick: (periodOpt: Option) => void;
  initHandler: () => void;
  applyHandler: () => void;
  dateValue: {
    startedAt: Dayjs | null;
    endedAt: Dayjs | null;
  };
  handleDatePickerChange: (key: string, date: Dayjs) => void;
  selectedPeriod: Option;
  orderFilterInput: OrderFilterInput;
}

function DetailSearchModal({
  isMobile,
  handleDetailModal,
  handlePeriodButtonClick,
  initHandler,
  applyHandler,
  dateValue,
  selectedPeriod,
  handleDatePickerChange,
  orderFilterInput,
}: Props) {
  const startDatePickerRef = useRef<HTMLDivElement>(null);
  const endDatePickerRef = useRef<HTMLDivElement>(null);

  const isDisabledInitButton = !(!dateValue.endedAt && !dateValue.startedAt);
  const isAbledApplyButton =
    (!!dateValue.endedAt && !!dateValue.startedAt) ||
    dayjs(orderFilterInput?.filter?.transactionTerm?.startedAt) !==
      dayjs(dateValue.startedAt);

  /* 인풋에서 직접 입력 방지 */
  const inputList = document.querySelectorAll('input.MuiInputBase-input');
  inputList.forEach(input => {
    (input as HTMLInputElement).disabled = true;
  });

  return (
    <ModalContainer
      isMobile={isMobile}
      isAbled={true}
      onClick={() => {}}
      onClose={() => {}}
      isBottomSheet
      style={{ gap: 0 }}
    >
      <ModalHeader>
        <Text size="20px" color="rgba(27, 28, 30, 1)" weight={600}>
          상세조회
        </Text>

        <CommonImage src={ICON_CLOSE} onClick={handleDetailModal} />
      </ModalHeader>

      <ModalBody>
        <PeriodButtonBox>
          {PERIOD_BUTTON_LIST.map(opt => (
            <PeriodButton
              key={opt.value}
              onClick={() => handlePeriodButtonClick(opt)}
              $isSelected={opt.value === selectedPeriod.value}
            >
              {opt.label}
            </PeriodButton>
          ))}
        </PeriodButtonBox>

        <DatePickerBox>
          <DatePickerWrap>
            <DesktopDatePicker
              format="YYYY-MM-DD"
              value={dateValue.startedAt}
              onChange={newValue => {
                handleDatePickerChange('startedAt', newValue);
              }}
              maxDate={!!dateValue.endedAt ? dayjs(dateValue.endedAt) : dayjs()}
              readOnly={selectedPeriod.value !== 'custom'}
            />
          </DatePickerWrap>
          ~
          <DatePickerWrap>
            <DesktopDatePicker
              format="YYYY-MM-DD"
              value={dateValue.endedAt}
              onChange={newValue => {
                handleDatePickerChange('endedAt', newValue);
              }}
              maxDate={dayjs()}
              minDate={
                !!dateValue.startedAt ? dayjs(dateValue.startedAt) : dayjs()
              }
              readOnly={selectedPeriod.value !== 'custom'}
            />
          </DatePickerWrap>
        </DatePickerBox>
      </ModalBody>

      <ModalFooter>
        <InitButton $isAbled={isDisabledInitButton} onClick={initHandler}>
          초기화
        </InitButton>

        <AuthButton
          name={'적용'}
          isAbled={isAbledApplyButton}
          onClick={applyHandler}
          style={{
            width: 'auto',
            minWidth: '76px',
            cursor: 'pointer',
            borderRadius: '12px',
          }}
        />
      </ModalFooter>
    </ModalContainer>
  );
}

export default DetailSearchModal;
